/**
 * @documental
 * @export Title
 * @import
 * import { Title } from "capitalrx-components-2.0/lib/typography";
 * @use
 * The `Title` component should be used as the main title for body content, alongside additional body content titles
 * [Subtitle](../Subtitle/README.md), [Heading](../Heading/README.md) and [Subheading](../Subheading/README.md).
 * @example
 * import { FC } from "react";
 * import { Title, Paragraph } from "capitalrx-components-2.0/lib/typography";
 *
 * export const Component: FC = () => {
 *   return (
 *     <div>
 *       <Title>Custom Title</Title>
 *       <Paragraph>
 *         The quick brown fox jumps over the lazy dog.
 *       </Paragraph>
 *     </div>
 *   );
 * };
 * @output
 * <div>
 *   <h2 class="title"><span class="semibold">Custom Title</span></h2>
 *   <p class="paragraph">
 *     The quick brown fox jumps over the lazy dog.
 *   </p>
 * </div>
 */
import { FC, PropsWithChildren } from "react";

import { BaseProps } from "../../BaseProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { Semibold } from "../Semibold";
import { TextAlignment } from "../TextAlignment";

import styles from "./styles.module.scss";

/**
 * The Title component properties.
 */
export interface TitleProps extends BaseProps {
  /**
   * The text alignment. If not specified defaults to `"left"`.
   */
  readonly textAlign?: TextAlignment;
}

/**
 * Displays title text. The title is automatically spaced apart from its siblings. However, if the title is the
 * first element it will not have any spacing above, and if it is the last element it will not have any spacing below.
 * Additionally, the title is unaffected by text sizing applied to the [Body](../Body/README.md) component.
 */
export const Title: FC<PropsWithChildren<TitleProps>> = (props) => {
  const { children, textAlign = "left" } = props;
  const baseHtmlProps = getHtmlPropsFromBaseProps(props, { className: [styles.Title, "title", `align-${textAlign}`] });

  return (
    <h2 {...baseHtmlProps}>
      <Semibold>{children}</Semibold>
    </h2>
  );
};
